/**
 * Family colour palette and null-safe resolution (WSRADM-0025).
 *
 * A non-#RRGGBB or null colour is never invented — consumers render the
 * neutral treatment. Contrast uses WCAG 2.x relative luminance.
 */

/** Fixed swatch palette for family colours (structure editor picks from these). */
export const FAMILY_PALETTE: readonly string[] = [
  '#2A9D8F',
  '#E76F51',
  '#F4A261',
  '#264653',
  '#8AB17D',
  '#E9C46A',
  '#6D597A',
  '#3A86FF',
  '#FF006E',
  '#8338EC',
  '#FB5607',
  '#118AB2',
];

/** Fixed 8-hue palette for dashboard check-in attendance bands (WSRADM-0037). */
export const DASHBOARD_BAND_PALETTE: readonly string[] = [
  '#2A9D8F',
  '#E76F51',
  '#F4A261',
  '#264653',
  '#8AB17D',
  '#E9C46A',
  '#6D597A',
  '#3A86FF',
];

export const DASHBOARD_OTHER_BAND_COLOR = '#9E9E9E';
export const DASHBOARD_UNKNOWN_BAND_COLOR = '#CFD8DC';

const HEX = /^#[0-9A-Fa-f]{6}$/;

function relativeLuminance(hex: string): number {
  const channel = (start: number): number => {
    const c = parseInt(hex.slice(start, start + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const r = channel(1);
  const g = channel(3);
  const b = channel(5);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Resolves a family colour for rendering. Returns null (the neutral "unset" treatment) when
 * `color` is null OR does not match /^#[0-9A-Fa-f]{6}$/. Only a valid #RRGGBB yields { bg, fg }.
 */
export function familyColorOrNeutral(color: string | null): { bg: string; fg: string } | null {
  if (color === null || !HEX.test(color)) return null;
  const L = relativeLuminance(color);
  return { bg: color, fg: L < 0.179 ? '#FFFFFF' : '#1A1A1A' };
}
